import React, { useCallback, useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import { colors, FlagIcon } from "@commonsku/styles";
import { BASE_COLUMN_TYPES, BaseColumnType } from "../../columns/config/types";
import { ReportColumnConfigBase } from "../../columns/config/ReportColumnConfig";
import { ClientColumn, SalesOrderNumberColumn, SubtotalColumn, TotalColumn } from "../common";
import { useReportContext } from "../../report_context";
import { Status } from "../../../../constants";
import { oauth } from "../../../../utils";
import { useHover } from "../../../../hooks";

const FlagContainer = styled.div`
  cursor: pointer;
  pointer-events: all;
  width: 24px;
  height: 24px;
`;

function FlagAction(
  {
    order_id,
    flag
  }
) {
  const [hoverRef, hover] = useHover<HTMLDivElement>();
  const [currentFlag, setFlag] = useState(flag === 1);

  const handleClick = useCallback(
    async () => {
      try {
        await oauth("PUT", `order/${order_id}`, { flag: currentFlag ? 0 : 1 });
        setFlag(!currentFlag);
      } catch (e) {
        toast.error("Unable to set flag");
      }
    },
    [currentFlag, order_id]
  );
   
  return (
    <FlagContainer ref={hoverRef} onClick={handleClick}>
      {(hover || currentFlag) && (
        <FlagIcon
          color={colors.error}
          filled={currentFlag}
        />
      )}
    </FlagContainer>
  );
}

const FlagColumn: ReportColumnConfigBase<
  typeof BASE_COLUMN_TYPES.Generic
> = {
  header: "Flag",
  type: BASE_COLUMN_TYPES.Generic,
  fields: [
    "order_id",
    "flag",
  ],
  accessorKey: "flag",
  canSort: true,
  orderByField: "flag",
  width: 65,
  avatarOptions: {
    withFullName: true,
  },
  justify: "center",
  dataTransform: ({
    order_id,
    flag,
  }) => ({
    cell: <FlagAction order_id={order_id} flag={flag} />
  }),
};


const ORepColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Avatar> = {
  header: <>Order&nbsp;Rep</>,
  type: BASE_COLUMN_TYPES.Avatar,
  fields: [
    "sales_rep_user_id",
    "sales_rep_first_name",
    "sales_rep_last_name",
    "sales_rep_avatar_file_id"
  ],
  accessorKey: "sales_rep",
  canSort: true,
  orderByField: "sales_rep",
  width: 175,
  avatarOptions: {
    withFullName: true,
  },
  dataTransform: ({
    sales_rep_first_name,
    sales_rep_last_name,
    sales_rep_avatar_file_id,
  }) => ({
    full_name: `${sales_rep_first_name} ${sales_rep_last_name}`,
    avatar_file_id: sales_rep_avatar_file_id,
  }),
};

const ProjectNumberColumn: ReportColumnConfigBase<
  typeof BASE_COLUMN_TYPES.Link
> = {
  header: <>Project&nbsp;#</>,
  type: BASE_COLUMN_TYPES.Link,
  fields: ["job_number"],
  accessorKey: "job_number",
  canSort: true,
  orderByField: "job_number",
  width: 83,
  justify: "center",
  dataTransform: ({ job_number }) => ({
    href: `/project/${job_number}`,
    text: job_number,
  }),
};

function DateColumnHeader() {
  const { prevQuery } = useReportContext();
  switch ("status_id" in prevQuery ? prevQuery.status_id : null) {
    case Status.SALES_ORDER_IN_PRODUCTION:
      return <>In-Hands&nbsp;Date</>;
    case Status.SALES_ORDER_SHIPPED:
      return <>Date&nbsp;Shipped</>;
    default:
      return <>Order&nbsp;Date</>;
  }
}

const OrderDateColumn: ReportColumnConfigBase<
  typeof BASE_COLUMN_TYPES.Date
> = {
  header: <>Order&nbsp;Date</>,
  type: BASE_COLUMN_TYPES.Date,
  fields: ["date_orderdate"],
  accessorKey: "date_orderdate",
  canSort: true,
  orderByField: "date_orderdate",
  width: 120,
  isVisible: (query: {}) => query['status_id'] === Status.SALES_ORDER_IN_PRODUCTION,
};

export const ReportDateColumn: ReportColumnConfigBase<
  typeof BASE_COLUMN_TYPES.Date
> = {
  header: <DateColumnHeader />,
  type: BASE_COLUMN_TYPES.Date,
  fields: ["date_reportdate"],
  accessorKey: "date_reportdate",
  canSort: true,
  orderByField: "date_reportdate",
  width: 120,
};

const ProjectNameColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Link> =
{
  header: "Project",
  type: BASE_COLUMN_TYPES.Link,
  fields: ["job_name", "job_number"],
  accessorKey: "job_name",
  canSort: true,
  orderByField: "job_name",
  width: 300,
  textOptions: {
    truncate: true,
 },
  dataTransform: ({ job_name, job_number }) => ({
    href: `/project/${job_number}`,
    text: job_name,
  }),
};

SalesOrderNumberColumn.header = <>Form&nbsp;#</>;
SalesOrderNumberColumn.width =  83;

export const Columns: ReportColumnConfigBase<BaseColumnType>[] = [
  FlagColumn,
  ORepColumn,
  SalesOrderNumberColumn,
  ProjectNumberColumn,
  ProjectNameColumn,
  ClientColumn,
  OrderDateColumn,
  ReportDateColumn,
  SubtotalColumn,
  TotalColumn,
];
